// Normalizes the camelCase API response into the PascalCase shapes our
// MI components expect. Single adapter layer — if the backend changes
// field names, only this file needs updating.

function num(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}
function pct(v) {
  const n = num(v);
  return n > 1 ? n : n * 100;
}

// --- KPIs -------------------------------------------------------------------
// The backend may return null metrics when no sales data exists for a range.
// We wrap each into the { Current, Previous, ChangePct, Direction, Format }
// shape our KpiTiles component reads.

function kpiMetric(current, previous, format) {
  const c = num(current);
  const p = num(previous);
  const changePct = p !== 0 ? (c - p) / p * 100 : c > 0 ? 100 : 0;
  const dir = Math.abs(changePct) < 1 ? 'flat' : changePct > 0 ? 'up' : 'down';
  return {
    Current: c,
    Previous: p,
    ChangePct: changePct,
    Direction: dir,
    Format: format
  };
}

// Map a structured KPI metric object (lowercase keys from backend) to the
// PascalCase shape the KpiTiles component reads.
function metricFromObject(m, defaultFormat) {
  if (!m || typeof m !== 'object') return null;
  return {
    Current: num(m.current !== undefined ? m.current : m.Current),
    Previous: num(m.previous !== undefined ? m.previous : m.Previous),
    ChangePct: num(m.changePct !== undefined ? m.changePct : m.ChangePct),
    Direction: m.direction || m.Direction || 'flat',
    Format: m.format || m.Format || defaultFormat
  };
}
export function normalizeKpis(raw, dashboard) {
  if (!raw) return null;

  // Structured KPI shape from staging: { current, previous, changePct,
  // direction, format } per metric. Most are populated; if a metric is
  // entirely zero we'll still trust it (the backend has the most
  // accurate view).
  const looksStructured = raw.revenue && typeof raw.revenue === 'object' && ('current' in raw.revenue || 'Current' in raw.revenue);
  if (looksStructured) {
    return {
      Revenue: metricFromObject(raw.revenue, 'currency'),
      Orders: metricFromObject(raw.orders, 'number'),
      Customers: metricFromObject(raw.customers, 'number'),
      WebsiteViews: metricFromObject(raw.websiteViews, 'number'),
      WebsiteSessions: metricFromObject(raw.websiteSessions, 'number'),
      ConversionRate: metricFromObject(raw.conversionRate, 'percent')
    };
  }

  // Legacy fallback: backend returned null/flat KPIs (old production
  // shape). Synthesize from geo + product + audience aggregates.
  const allNull = Object.values(raw).every(v => v === null || v === undefined);
  if (allNull && dashboard) {
    const geo = dashboard.geoByPostalCode || [];
    const products = dashboard.trendingProducts || [];
    const audiences = dashboard.audiences || [];
    const views = geo.reduce((s, g) => s + num(g.views), 0);
    const sessions = geo.reduce((s, g) => s + num(g.sessions), 0);
    const conversions = geo.reduce((s, g) => s + num(g.conversions), 0);
    const revenue = products.reduce((s, p) => s + num(p.totalAmount || p.revenue), 0);
    const orders = products.reduce((s, p) => s + num(p.orders), 0);
    const customers = products.reduce((s, p) => s + num(p.customers), 0);
    const convRate = views > 0 ? conversions / views * 100 : 0;

    // Previous-period values from audience prevConversions / prevViews
    const prevViews = audiences.reduce((s, a) => s + num(a.prevViews), 0);
    const prevSessions = audiences.reduce((s, a) => s + num(a.prevSessions), 0);
    const prevConv = audiences.reduce((s, a) => s + num(a.prevConversions), 0);
    const prevRevenue = products.reduce((s, p) => s + num(p.prevTotalAmount), 0);
    const prevConvRate = prevViews > 0 ? prevConv / prevViews * 100 : 0;
    return {
      Revenue: kpiMetric(revenue, prevRevenue, 'currency'),
      Orders: kpiMetric(orders, null, 'number'),
      Customers: kpiMetric(customers, null, 'number'),
      WebsiteViews: kpiMetric(views, prevViews, 'number'),
      WebsiteSessions: kpiMetric(sessions, prevSessions, 'number'),
      ConversionRate: kpiMetric(convRate, prevConvRate, 'percent')
    };
  }
  return {
    Revenue: kpiMetric(raw.revenue, null, 'currency'),
    Orders: kpiMetric(raw.orders, null, 'number'),
    Customers: kpiMetric(raw.customers, null, 'number'),
    WebsiteViews: kpiMetric(raw.websiteViews, null, 'number'),
    WebsiteSessions: kpiMetric(raw.websiteSessions, null, 'number'),
    ConversionRate: kpiMetric(raw.conversionRate, null, 'percent')
  };
}

// --- Trending / Underperforming Products ------------------------------------

function normalizeProduct(p) {
  return {
    ...p,
    Title: p.title || '',
    Category: p.category || '',
    ImageLink: p.imageLink || '',
    Price: num(p.price),
    Views: num(p.views),
    Conversions: num(p.conversions),
    ConversionRate: num(p.conversionRate),
    SalesQuantity: num(p.salesQuantity),
    Revenue: num(p.totalAmount || p.revenue),
    SalesGrowthPct: num(p.salesGrowthPct),
    MomentumScore: num(p.momentumScore),
    TrendDirection: p.trendDirection || 'stable'
  };
}
export function normalizeProducts(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeProduct);
}

// --- Category Performance ---------------------------------------------------

function shortCategory(full) {
  if (!full) return '';
  const parts = full.split('>').map(s => s.trim());
  return parts[parts.length - 1] || full;
}
function normalizeCategory(c) {
  const fullName = c.category || c.name || '';
  return {
    ...c,
    CategoryName: shortCategory(fullName),
    FullCategoryName: fullName,
    Name: shortCategory(fullName),
    Revenue: num(c.totalRevenue),
    ProductCount: num(c.productCount),
    AvgConversionRate: num(c.avgConversionRate),
    TrendDirection: c.trendDirection || 'stable'
  };
}
export function normalizeCategories(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeCategory);
}

// --- Campaigns --------------------------------------------------------------

function normalizeCampaign(c) {
  return {
    ...c,
    Campaign: c.campaign || c.name || '',
    Platform: c.type || c.platform || '',
    Impressions: num(c.impressions),
    Clicks: num(c.clicks),
    CTR: pct(c.ctr),
    Cost: num(c.cost),
    Conversions: num(c.conversions || c.conversionRate),
    CPC: num(c.averageCpc || c.cpc || num(c.cost) / Math.max(1, num(c.clicks)))
  };
}
export function normalizeCampaigns(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeCampaign);
}

// --- Campaign ROI -----------------------------------------------------------

function normalizeCampaignROI(c) {
  return {
    ...c,
    Category: c.category || c.type || '',
    Spend: num(c.spend),
    Revenue: num(c.revenue),
    ROAS: num(c.roas),
    CTR: pct(c.ctr),
    CPA: num(c.cpa)
  };
}
export function normalizeCampaignROIList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeCampaignROI);
}

// --- Audiences --------------------------------------------------------------

function normalizeAudience(a) {
  return {
    ...a,
    AudienceName: a.audienceName || a.name || '',
    Name: a.audienceName || a.name || '',
    Conversions: num(a.conversions),
    Views: num(a.views),
    Sessions: num(a.sessions),
    Revenue: num(a.conversionsPriceValue),
    ConversionsGrowthPct: num(a.conversionsGrowthPct),
    ViewsGrowthPct: num(a.viewsGrowthPct),
    TrendDirection: (() => {
      const g = num(a.conversionsGrowthPct);
      if (g > 5) return 'rising';
      if (g < -5) return 'declining';
      return 'stable';
    })()
  };
}
export function normalizeAudiences(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeAudience);
}

// --- Geo --------------------------------------------------------------------

function normalizeGeoRow(g) {
  const views = num(g.views);
  const conv = num(g.conversions);
  return {
    ...g,
    PostalCode: g.postalCode || '',
    City: g.city || '',
    Region: g.regionCode || g.region || '',
    Views: views,
    Sessions: num(g.sessions),
    Conversions: conv,
    ConversionRate: views > 0 ? conv / views * 100 : 0,
    UniqueUsers: num(g.uniqueUsers)
  };
}
export function normalizeGeo(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeGeoRow);
}

// --- Diagnostics ------------------------------------------------------------

function normalizeDiagnostic(d) {
  return {
    ...d,
    Severity: d.severity || 'info',
    Finding: d.finding || d.title || '',
    Recommendation: d.recommendation || d.description || '',
    Details: d.supportingData || d.details || null
  };
}
export function normalizeDiagnostics(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeDiagnostic);
}

// --- Comparison (period-over-period) ----------------------------------------
// The backend returns { website: { metrics: {...} }, sales: { metrics: {...} } }.
// We flatten into the shape PeriodComparison expects.

export function normalizeComparison(raw, dashboard) {
  // Staging shape: { website: { views: {current,previous,changePct},
  // sessions: {...}, ... }, sales: { revenue: {...}, ... }, product: {...} }.
  // Older production shape: same but wrapped in `.metrics`. Handle both.
  if (raw) {
    const sectionMetrics = section => {
      if (!section) return {};
      // Old shape had a `.metrics` wrapper; new shape is flat.
      if (section.metrics && typeof section.metrics === 'object') return section.metrics;
      return section;
    };
    const web = sectionMetrics(raw.website);
    const sales = sectionMetrics(raw.sales);
    const product = sectionMetrics(raw.product);
    const hasData = Object.keys(web).length > 0 || Object.keys(sales).length > 0 || Object.keys(product).length > 0;
    if (hasData) {
      // Prefer the headline metrics in a fixed order so the chart
      // always shows the same axes regardless of which sections the
      // backend populates.
      const preferred = [['Revenue', sales.revenue], ['Orders', sales.orders], ['Views', web.views], ['Sessions', web.sessions], ['Conversions', web.conversions]];
      const categories = [];
      const current = [];
      const previous = [];
      for (const [name, vals] of preferred) {
        if (!vals || typeof vals !== 'object') continue;
        categories.push(name);
        current.push(num(vals.current));
        previous.push(num(vals.previous));
      }
      if (categories.length > 0) {
        return {
          Categories: categories,
          Current: current,
          Previous: previous
        };
      }
    }
  }

  // Fallback: synthesize from geo + audience prev* fields.
  if (!dashboard) return null;
  const geo = dashboard.geoByPostalCode || [];
  const audiences = dashboard.audiences || [];
  const products = dashboard.trendingProducts || [];
  const curViews = geo.reduce((s, g) => s + num(g.views), 0);
  const curSessions = geo.reduce((s, g) => s + num(g.sessions), 0);
  const curConversions = geo.reduce((s, g) => s + num(g.conversions), 0);
  const curRevenue = products.reduce((s, p) => s + num(p.totalAmount || p.revenue), 0);
  const curOrders = products.reduce((s, p) => s + num(p.orders), 0);
  const prevViews = audiences.reduce((s, a) => s + num(a.prevViews), 0);
  const prevSessions = audiences.reduce((s, a) => s + num(a.prevSessions), 0);
  const prevConversions = audiences.reduce((s, a) => s + num(a.prevConversions), 0);
  const prevRevenue = products.reduce((s, p) => s + num(p.prevTotalAmount), 0);
  const prevOrders = products.reduce((s, p) => s + num(p.prevOrders), 0);
  const hasPrev = prevViews > 0 || prevSessions > 0 || prevConversions > 0;
  if (!hasPrev && curViews === 0) return null;
  return {
    Categories: ['Revenue', 'Orders', 'Views', 'Sessions', 'Conversions'],
    Current: [curRevenue, curOrders, curViews, curSessions, curConversions],
    Previous: [prevRevenue, prevOrders, prevViews, prevSessions, prevConversions]
  };
}

// --- Locations --------------------------------------------------------------

export function normalizeLocations(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.locations)) return raw.locations;
  if (raw && Array.isArray(raw.items)) return raw.items;
  if (raw && Array.isArray(raw.Locations)) return raw.Locations;
  return [];
}

// --- Full dashboard ---------------------------------------------------------

export function normalizeDashboard(raw) {
  if (!raw) return raw;
  return {
    ...raw,
    LocationName: raw.locationName,
    Range: raw.range,
    ComparisonType: raw.comparisonType,
    GeneratedAt: raw.generatedAt,
    Kpis: normalizeKpis(raw.kpis, raw),
    TrendingProducts: normalizeProducts(raw.trendingProducts),
    UnderperformingProducts: normalizeProducts(raw.underperformingProducts),
    CategoryPerformance: normalizeCategories(raw.categoryPerformance),
    Campaigns: normalizeCampaigns(raw.campaigns),
    CampaignROI: normalizeCampaignROIList(raw.campaignROI),
    Audiences: normalizeAudiences(raw.audiences),
    GeoByPostalCode: normalizeGeo(raw.geoByPostalCode),
    GeoByRegion: normalizeGeo(raw.geoByRegion),
    Diagnostics: normalizeDiagnostics(raw.diagnostics),
    Comparison: normalizeComparison(raw.comparison, raw),
    ExecutiveSummary: raw.executiveSummary || null,
    FullReport: raw.fullReport || null
  };
}